import { type DropdownEntry } from "~/common/types";
import { type SalesPartnerInfo, type UserInfoData } from "./types";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { type SalesPartner, SalesPartnerStatus } from "~/feature/Analytics/ga/salesPartner";
import { type GoogleMarker } from "~/SalesPartner/salesPartnerTypes";

export const transformUserInfoData = (data: UserInfoData) => {
  const object = {
    firstName: data.firstName,
    lastName: data.lastName,
    company: data.company,
    line1: data.line1, // Line 1 contains house number and street
    line2: data.additionalLine1, // Additional address line (displayed after Line1)
    zipCode: data.zipCode,
    city: data.city,
    country: data.country,
    salutationCode: data.salutationCode,
    titleCode: data.titleCode,
    phone: data.phone,
  };

  return object;
};

export const transformSalesPartnerData = (data: SalesPartnerInfo): SalesPartner => {
  const { affiliateId: id, displayName: name } = data;
  return {
    id,
    name,
    status: SalesPartnerStatus.SAVED,
  };
};

export const transformSelectedMarkerData = (
  data?: GoogleMarker,
  savedAffiliate?: SalesPartnerDTO,
): SalesPartner | undefined => {
  if (!data) {
    return undefined;
  }
  const isPreferred = data.affiliateId === savedAffiliate?.affiliateId;

  return {
    id: data.subsidiaryId,
    name: data.subsidiaryName,
    status: isPreferred ? SalesPartnerStatus.SAVED : null,
  };
};

export const transformMarkerListData = (markers: GoogleMarker[], savedAffiliate?: SalesPartnerDTO): SalesPartner[] => {
  return markers.map((marker) => ({
    id: marker.subsidiaryId,
    name: marker.subsidiaryName,
    status: marker.affiliateId === savedAffiliate?.affiliateId ? SalesPartnerStatus.SAVED : null,
  }));
};

export const useCountryOptions = (): DropdownEntry<string>[] => {
  const { t } = useTranslation();
  return useMemo(() => {
    return window.SCHA.countryOptions.map((option) => ({
      value: option.isocode,
      label: t(option.name),
      stringLabel: t(option.name),
    }));
  }, []);
};
